import { z } from "zod";
import { sectionIsNotApplicableValueToDisplayValue } from "../helpers/recordingAssessmentFeedbackHelpers.js";
import {
  recordingIdSchema
} from "./recording.js";
import { teammateIdSchema } from "./teammate.js";
const recordingAssessmentFeedbackStatusSchema = z.enum([
  "open",
  "resolved",
  "outdated"
]);
const statusChangeBaseFields = {
  /** The status this change moved the row to; the column holds the latest. */
  status: recordingAssessmentFeedbackStatusSchema,
  changed_at: z.iso.datetime(),
  /** Optional context from whoever or whatever made the change. */
  note: z.string().optional()
};
const recordingAssessmentFeedbackStatusChangeSchema = z.discriminatedUnion(
  "reason",
  [
    /** A human resolved it. */
    z.object({
      reason: z.literal("manual"),
      teammate_id: teammateIdSchema,
      ...statusChangeBaseFields
    }),
    /** The live grade matches the feedback, at submit or after a regrade. */
    z.object({
      reason: z.literal("matched_grading"),
      ...statusChangeBaseFields
    }),
    /** The section/criteria it targets changed or was removed. */
    z.object({
      reason: z.literal("playbook_change"),
      /** The revision that outdated it. */
      playbook_revision_id: z.uuid(),
      ...statusChangeBaseFields
    })
  ]
);
const recordingAssessmentFeedbackSubmissionSchema = z.object({
  /** The original criteria configuration at the time of the feedback. */
  criteria_node: z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().optional(),
    grading_context: z.string().optional(),
    grading_value_type: z.enum(["section", "yes_no", "1_5_score"])
  }),
  /** The original grading at the time of the feedback. */
  grading: z.object({
    value: z.string(),
    commentary: z.string()
  }),
  /** The value the reviewer says is right; "a" / "n/a" for a section. */
  correct_value: z.string(),
  explanation: z.string()
});
const recordingAssessmentFeedbackDataSchema = recordingAssessmentFeedbackSubmissionSchema.extend({
  grading: recordingAssessmentFeedbackSubmissionSchema.shape.grading.extend({
    /** The recordings the graded interaction held at submit time. */
    recording_ids: z.array(recordingIdSchema).optional(),
    /** The playbook revision that was live at submit time. */
    live_revision_id: z.uuid().optional()
  }),
  /** The row's lifecycle transitions, newest last. */
  status_changes: z.array(recordingAssessmentFeedbackStatusChangeSchema).optional()
});
function applyFeedbackSubmission(stored, submission) {
  return {
    ...stored,
    criteria_node: submission.criteria_node,
    correct_value: submission.correct_value,
    explanation: submission.explanation,
    grading: {
      ...stored.grading,
      value: submission.grading.value,
      commentary: submission.grading.commentary
    }
  };
}
function checkIsSectionFeedback(feedback) {
  return feedback.data.criteria_node.grading_value_type === "section";
}
function checkIsCriteriaFeedback(feedback) {
  return feedback.data.criteria_node.grading_value_type !== "section";
}
function checkIsSectionFeedbackWithCurrentGrade(feedback) {
  return checkIsSectionFeedback(feedback);
}
function checkIsCriteriaFeedbackWithCurrentGrade(feedback) {
  return checkIsCriteriaFeedback(feedback);
}
const sectionFeedbackCorrectValueToDisplayValue = (value) => {
  if (value === void 0) {
    return void 0;
  }
  const isNotApplicable = value === "n/a";
  return sectionIsNotApplicableValueToDisplayValue(isNotApplicable);
};
export {
  applyFeedbackSubmission,
  checkIsCriteriaFeedback,
  checkIsCriteriaFeedbackWithCurrentGrade,
  checkIsSectionFeedback,
  checkIsSectionFeedbackWithCurrentGrade,
  recordingAssessmentFeedbackSubmissionSchema,
  sectionFeedbackCorrectValueToDisplayValue
};
//# sourceMappingURL=recordingAssessmentFeedback.js.map
